import { useForm } from '@tanstack/react-form';
import { log } from 'evlog/client';
import { CalendarSearch } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useLoaderData, useNavigate, useParams } from 'react-router';
import { z } from 'zod';

import { displayName, toAge } from '@virtual-care/isomorphic';
import { AttachFilesField, uploadFile } from '@virtual-care/media-web';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Checkbox,
  Input,
  Label,
  RadioGroup,
  RadioGroupItem,
  Textarea,
} from '@virtual-care/shadcn';
import {
  BirthDateField,
  FieldMessage,
  FormMessage,
  GenderField,
  errorToFormIssue,
} from '@virtual-care/ui';

import { requiresAuth } from '../../auth-loader.ts';
import { ProfileCard } from '../../components/header';
import Header from '../../components/header/header';
import * as inputs from '../../components/inputs.ts';
import { PrivacyNote, SymptomHints } from '../../components/symptom-form.tsx';
import { secondOpinionReservationStore } from '../../hooks/reservation/second-opinion.ts';

// Separate schemas for B2C and B2B variants
const symptomSchemaB2C = z.object({
  name: z.string(),
  age: z.string(),
  gender: z.string(),
  symptoms: z
    .string()
    .min(10, { message: 'booking.symptomChecker.validation.symptomsMin' }),
  files: inputs.files,
  urgent: z.boolean(),
});

const symptomSchemaOnBehalf = z.object({
  patientName: z
    .string()
    .min(1, { message: 'booking.symptomChecker.validation.patientName' }),
  patientBirthDate: z
    .string()
    .min(1, { message: 'booking.symptomChecker.validation.patientBirthDate' }),
  patientGender: z.enum(['male', 'female'], {
    message: 'booking.symptomChecker.validation.patientGender',
  }),
  symptoms: z
    .string()
    .min(10, { message: 'booking.symptomChecker.validation.symptomsMin' }),
  files: inputs.files,
  urgent: z.boolean(),
});

const symptomSchemaB2B = symptomSchemaOnBehalf.extend({
  doctorName: z
    .string()
    .min(1, { message: 'booking.symptomChecker.validation.doctorName' }),
});

function B2CSecondOpinionChecker() {
  const { t } = useTranslation();
  const [bookingFor, setBookingFor] = useState<'self' | 'other'>('self');

  const toggle = (
    <RadioGroup
      value={bookingFor}
      onValueChange={(v) => {
        if (v === 'self' || v === 'other') setBookingFor(v);
      }}
      className="flex gap-4"
    >
      <div className="flex items-center gap-2">
        <RadioGroupItem value="self" id="so-self" />
        <Label htmlFor="so-self" className="cursor-pointer">
          {t('booking.symptomChecker.bookingForSelf')}
        </Label>
      </div>
      <div className="flex items-center gap-2">
        <RadioGroupItem value="other" id="so-other" />
        <Label htmlFor="so-other" className="cursor-pointer">
          {t('booking.symptomChecker.bookingForOther')}
        </Label>
      </div>
    </RadioGroup>
  );

  if (bookingFor === 'other')
    return <B2COnBehalfSecondOpinionChecker toggle={toggle} />;
  return <B2CSelfSecondOpinionChecker toggle={toggle} />;
}

function SecondOpinionLayout({
  toggle,
  children,
}: {
  toggle: React.ReactNode;
  children: React.ReactNode;
}) {
  const { t } = useTranslation();
  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-sidebar-primary rounded-full p-2">
              <CalendarSearch className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('secondOpinion.pageTitle')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>
      <div className="card-container">
        <Card className="card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-bold">
              {t('secondOpinion.symptomChecker.title')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t('symptomForm.subtitle')}
            </p>
          </CardHeader>
          <CardContent className="flex flex-col gap-5">
            {toggle}
            {children}
          </CardContent>
        </Card>
      </div>
    </>
  );
}

function B2CSelfSecondOpinionChecker({ toggle }: { toggle: React.ReactNode }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const data = useLoaderData() as Awaited<
    ReturnType<typeof secondOpinionSymptomCheckerLoader>
  >;
  const { specialtyId } = useParams();

  const fullName = displayName(data.identity);
  const age = data.identity.b2cProfile
    ? `${toAge(data.identity.b2cProfile)} ${t('booking.symptomChecker.fields.ageSuffix')}`
    : '';
  const rawGender = data.identity.b2cProfile?.gender;
  const gender =
    rawGender === 'male' || rawGender === 'female'
      ? t(`common.${rawGender}`)
      : '';

  const form = useForm({
    defaultValues: {
      name: fullName,
      age,
      gender,
      symptoms: import.meta.env.DEV
        ? 'الرجاء تعبئة البيانات وإرفاق الملفات إن وُجدت'
        : '',
      files: [],
      fileIds: [],
      urgent: false,
    } as z.infer<typeof symptomSchemaB2C>,
    validators: {
      onSubmit: symptomSchemaB2C,
      onSubmitAsync: async ({ value }) => {
        try {
          log.info({
            action: 'symptom_checker_submit',
            service: 'second_opinion',
            bookingFor: 'self',
            specialtyId,
            fileCount: value.files?.length ?? 0,
            urgent: value.urgent,
          });
          const fileIds = await Promise.all(
            (value.files ?? []).map((file) => uploadFile(file)),
          );

          secondOpinionReservationStore.write({
            description: value.symptoms,
            specialityId: specialtyId ?? '',
            files: fileIds,
            urgent: value.urgent,
          });

          navigate('/second-opinion/booking-confirmation');
          return void 0;
        } catch (error) {
          log.error({
            action: 'symptom_checker_submit',
            service: 'second_opinion',
            error,
          });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <SecondOpinionLayout toggle={toggle}>
      <form
        className="space-y-6"
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 gap-6">
          <form.Field
            name="name"
            children={(field) => (
              <div>
                <Label htmlFor="name" className="text-sm font-bold">
                  {t('booking.symptomChecker.fields.name')}
                </Label>
                <Input
                  id="name"
                  value={field.state.value}
                  disabled
                  className="mt-2 bg-gray-100"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="age"
            children={(field) => (
              <div>
                <Label htmlFor="age" className="text-sm font-bold">
                  {t('booking.symptomChecker.fields.age')}
                </Label>
                <Input
                  id="age"
                  value={field.state.value}
                  disabled
                  className="mt-2 bg-gray-100"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="gender"
            children={(field) => (
              <div>
                <Label htmlFor="gender" className="text-sm font-bold">
                  {t('booking.symptomChecker.fields.gender')}
                </Label>
                <Input
                  id="gender"
                  value={field.state.value}
                  disabled
                  className="mt-2 bg-gray-100"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
        </div>

        <form.Field
          name="symptoms"
          children={(field) => (
            <div className="space-y-2">
              <Label htmlFor="symptoms">
                {t('booking.symptomChecker.fields.symptoms')}
              </Label>
              <SymptomHints
                hints={[
                  t('symptomForm.hints.suffering'),
                  t('symptomForm.hints.chronic'),
                  t('symptomForm.hints.onset'),
                ]}
              />
              <Textarea
                id="symptoms"
                placeholder={t(
                  'booking.symptomChecker.fields.symptomsPlaceholder',
                )}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className="min-h-[120px] resize-none"
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="files"
          children={(field) => (
            <div className="space-y-2">
              <AttachFilesField field={field} />
              <p className="text-xs text-gray-500">
                {t('symptomForm.attachmentHint')}
              </p>
            </div>
          )}
        />

        <form.Field
          name="urgent"
          children={(field) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="urgent"
                checked={field.state.value}
                onCheckedChange={(checked) => field.handleChange(!!checked)}
              />
              <Label htmlFor="urgent" className="cursor-pointer">
                {t('secondOpinion.symptomChecker.urgentCheckbox')}
              </Label>
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <PrivacyNote text={t('symptomForm.privacyNote')} />
        <div className="flex justify-center gap-3 pt-4">
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="cursor-pointer rounded-lg px-6 py-4 text-base text-gray-600 hover:bg-gray-100"
                >
                  {t('booking.symptomChecker.back')}
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="cursor-pointer rounded-lg bg-green-600 px-8 py-4 text-base hover:bg-green-700"
                >
                  {isSubmitting
                    ? t('booking.symptomChecker.submitting')
                    : t('secondOpinion.symptomChecker.continue')}
                </Button>
              </>
            )}
          />
        </div>
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => <FormMessage errors={errors} />}
        />
      </form>
    </SecondOpinionLayout>
  );
}

function B2COnBehalfSecondOpinionChecker({
  toggle,
}: {
  toggle: React.ReactNode;
}) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { specialtyId } = useParams();

  const form = useForm({
    defaultValues: {
      patientName: '',
      patientBirthDate: '',
      patientGender: '' as 'male' | 'female' | '',
      symptoms: import.meta.env.DEV
        ? 'الرجاء تعبئة البيانات وإرفاق الملفات إن وُجدت'
        : '',
      files: [] as File[],
      urgent: false,
    } as z.infer<typeof symptomSchemaOnBehalf>,
    validators: {
      onSubmit: symptomSchemaOnBehalf,
      onSubmitAsync: async ({ value }) => {
        try {
          log.info({
            action: 'symptom_checker_submit',
            service: 'second_opinion',
            bookingFor: 'other',
            specialtyId,
            fileCount: value.files?.length ?? 0,
            urgent: value.urgent,
          });
          const fileIds = await Promise.all(
            (value.files ?? []).map((file) => uploadFile(file)),
          );

          sessionStorage.setItem(
            'reservation',
            JSON.stringify({
              description: value.symptoms,
              specialityId: specialtyId ?? '',
              service: 'second_opinion',
              files: fileIds,
              urgent: value.urgent,
              patientName: value.patientName,
              patientBirthDate: new Date(value.patientBirthDate).toISOString(),
              patientGender: value.patientGender,
            }),
          );

          navigate('/second-opinion/booking-confirmation');
          return void 0;
        } catch (error) {
          log.error({
            action: 'symptom_checker_submit',
            service: 'second_opinion',
            error,
          });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <SecondOpinionLayout toggle={toggle}>
      <form
        className="space-y-6"
        onSubmit={async (e) => {
          e.preventDefault();
          e.stopPropagation();
          await form.handleSubmit();
        }}
      >
        <div className="grid grid-cols-1 gap-6">
          <form.Field
            name="patientName"
            children={(field) => (
              <div>
                <Label htmlFor="patientName" className="text-sm font-bold">
                  {t('booking.symptomChecker.fields.patientName')}
                </Label>
                <Input
                  id="patientName"
                  placeholder={t(
                    'booking.symptomChecker.fields.patientNamePlaceholder',
                  )}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  className="mt-2"
                />
                <FieldMessage errors={field.state.meta.errors} />
              </div>
            )}
          />
          <form.Field
            name="patientBirthDate"
            children={(field) => <BirthDateField field={field} star />}
          />
          <form.Field
            name="patientGender"
            children={(field) => (
              <GenderField
                field={field}
                label={t('common.gender')}
                placeholder={t('common.select')}
                maleLabel={t('common.male')}
                femaleLabel={t('common.female')}
              />
            )}
          />
        </div>

        <form.Field
          name="symptoms"
          children={(field) => (
            <div className="space-y-2">
              <Label htmlFor="symptoms">
                {t('booking.symptomChecker.fields.symptoms')}
              </Label>
              <SymptomHints
                hints={[
                  t('symptomForm.hints.suffering'),
                  t('symptomForm.hints.chronic'),
                  t('symptomForm.hints.onset'),
                ]}
              />
              <Textarea
                id="symptoms"
                placeholder={t(
                  'booking.symptomChecker.fields.symptomsPlaceholder',
                )}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                className="min-h-[120px] resize-none"
              />
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <form.Field
          name="files"
          children={(field) => (
            <div className="space-y-2">
              <AttachFilesField field={field} />
              <p className="text-xs text-gray-500">
                {t('symptomForm.attachmentHint')}
              </p>
            </div>
          )}
        />

        <form.Field
          name="urgent"
          children={(field) => (
            <div className="flex items-center gap-2">
              <Checkbox
                id="urgent"
                checked={field.state.value}
                onCheckedChange={(checked) => field.handleChange(!!checked)}
              />
              <Label htmlFor="urgent" className="cursor-pointer">
                {t('secondOpinion.symptomChecker.urgentCheckbox')}
              </Label>
              <FieldMessage errors={field.state.meta.errors} />
            </div>
          )}
        />

        <PrivacyNote text={t('symptomForm.privacyNote')} />
        <div className="flex justify-center gap-3 pt-4">
          <form.Subscribe
            selector={(state) => [state.canSubmit, state.isSubmitting]}
            children={([canSubmit, isSubmitting]) => (
              <>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                  className="cursor-pointer rounded-lg px-6 py-4 text-base text-gray-600 hover:bg-gray-100"
                >
                  {t('booking.symptomChecker.back')}
                </Button>
                <Button
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                  className="cursor-pointer rounded-lg bg-green-600 px-8 py-4 text-base hover:bg-green-700"
                >
                  {isSubmitting
                    ? t('booking.symptomChecker.submitting')
                    : t('secondOpinion.symptomChecker.continue')}
                </Button>
              </>
            )}
          />
        </div>
        <form.Subscribe
          selector={(state) => state.errors}
          children={(errors) => <FormMessage errors={errors} />}
        />
      </form>
    </SecondOpinionLayout>
  );
}

function B2BSecondOpinionChecker() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { specialtyId } = useParams();

  const form = useForm({
    defaultValues: {
      doctorName: '',
      patientName: '',
      patientBirthDate: '',
      patientGender: '' as 'male' | 'female' | '',
      symptoms: '',
      files: [] as File[],
      urgent: false,
    } as z.infer<typeof symptomSchemaB2B>,
    validators: {
      onSubmit: symptomSchemaB2B,
      onSubmitAsync: async ({ value }) => {
        try {
          log.info({
            action: 'symptom_checker_submit',
            service: 'second_opinion',
            userType: 'B2B',
            specialtyId,
            fileCount: value.files?.length ?? 0,
            urgent: value.urgent,
          });
          const fileIds = await Promise.all(
            (value.files ?? []).map((file) => uploadFile(file)),
          );

          sessionStorage.setItem(
            'reservation',
            JSON.stringify({
              description: value.symptoms,
              specialityId: specialtyId ?? '',
              service: 'second_opinion',
              files: fileIds,
              urgent: value.urgent,
              doctorName: value.doctorName,
              patientName: value.patientName,
              patientBirthDate: new Date(value.patientBirthDate).toISOString(),
              patientGender: value.patientGender,
            }),
          );

          navigate('/second-opinion/booking-confirmation');

          return void 0;
        } catch (error) {
          log.error({
            action: 'symptom_checker_submit',
            service: 'second_opinion',
            error,
          });
          return errorToFormIssue(error);
        }
      },
    },
  });

  return (
    <>
      <Header
        title={
          <div className="flex items-center gap-2">
            <div className="bg-sidebar-primary rounded-full p-2">
              <CalendarSearch className="h-6 w-6 text-white" />
            </div>
            <h1 className="text-base font-bold text-gray-900 md:text-2xl">
              {t('secondOpinion.pageTitle')}
            </h1>
          </div>
        }
      >
        <ProfileCard />
      </Header>
      <div className="card-container">
        <Card className="card">
          <CardHeader className="text-center">
            <CardTitle className="text-xl font-bold">
              {t('secondOpinion.symptomChecker.title')}
            </CardTitle>
            <p className="text-sm text-gray-500">
              {t('symptomForm.subtitle')}
            </p>
          </CardHeader>

          <CardContent className="flex flex-col gap-5">
            <div className="flex flex-col gap-3 text-sm">
              <div className="flex items-center justify-between rounded-md border border-green-500 bg-green-50/50 px-4 py-2">
                <p className="text-gray-600">
                  <Trans
                    i18nKey="secondOpinion.symptomChecker.priceSummaryNormal"
                    components={{
                      strong: <span className="font-bold text-green-700" />,
                    }}
                  />
                </p>
              </div>
              <div className="flex items-center justify-between rounded-md border border-green-500 bg-green-50/50 px-4 py-2">
                <p className="text-gray-600">
                  <Trans
                    i18nKey="secondOpinion.symptomChecker.priceSummaryUrgent"
                    components={{
                      strong: <span className="font-bold text-green-700" />,
                    }}
                  />
                </p>
              </div>
            </div>

            <form
              className="space-y-6"
              onSubmit={async (e) => {
                e.preventDefault();
                e.stopPropagation();
                await form.handleSubmit();
              }}
            >
              <div className="grid grid-cols-1 gap-6">
                <form.Field
                  name="doctorName"
                  children={(field) => (
                    <div>
                      <Label htmlFor="doctorName" className="text-sm font-bold">
                        {t('booking.symptomChecker.fields.doctorName')}
                      </Label>
                      <Input
                        id="doctorName"
                        placeholder={t(
                          'booking.symptomChecker.fields.doctorNamePlaceholder',
                        )}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        className="mt-2"
                      />
                      <FieldMessage errors={field.state.meta.errors} />
                    </div>
                  )}
                />
                <form.Field
                  name="patientName"
                  children={(field) => (
                    <div>
                      <Label
                        htmlFor="patientName"
                        className="text-sm font-bold"
                      >
                        {t('booking.symptomChecker.fields.patientName')}
                      </Label>
                      <Input
                        id="patientName"
                        placeholder={t(
                          'booking.symptomChecker.fields.patientNamePlaceholder',
                        )}
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={(e) => field.handleChange(e.target.value)}
                        className="mt-2"
                      />
                      <FieldMessage errors={field.state.meta.errors} />
                    </div>
                  )}
                />
                <form.Field
                  name="patientBirthDate"
                  children={(field) => <BirthDateField field={field} star />}
                />
                <form.Field
                  name="patientGender"
                  children={(field) => (
                    <GenderField
                      field={field}
                      label={t('common.gender')}
                      placeholder={t('common.select')}
                      maleLabel={t('common.male')}
                      femaleLabel={t('common.female')}
                    />
                  )}
                />
              </div>

              <form.Field
                name="symptoms"
                children={(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="symptoms">
                      {t('booking.symptomChecker.fields.symptoms')}
                    </Label>
                    <SymptomHints
                      hints={[
                        t('symptomForm.hints.suffering'),
                        t('symptomForm.hints.chronic'),
                        t('symptomForm.hints.onset'),
                      ]}
                    />
                    <Textarea
                      id="symptoms"
                      placeholder={t(
                        'booking.symptomChecker.fields.symptomsPlaceholder',
                      )}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      className="min-h-[120px] resize-none"
                    />
                    <FieldMessage errors={field.state.meta.errors} />
                  </div>
                )}
              />

              <form.Field
                name="files"
                children={(field) => (
                  <div className="space-y-2">
                    <AttachFilesField field={field} />
                    <p className="text-xs text-gray-500">
                      {t('symptomForm.attachmentHint')}
                    </p>
                  </div>
                )}
              />

              <form.Field
                name="urgent"
                children={(field) => (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="urgent"
                      checked={field.state.value}
                      onCheckedChange={(checked) =>
                        field.handleChange(!!checked)
                      }
                    />
                    <Label htmlFor="urgent" className="cursor-pointer">
                      {t('secondOpinion.symptomChecker.urgentCheckbox')}
                    </Label>
                    <FieldMessage errors={field.state.meta.errors} />
                  </div>
                )}
              />

              <PrivacyNote text={t('symptomForm.privacyNote')} />
              <div className="flex justify-center gap-3 pt-4">
                <form.Subscribe
                  selector={(state) => [state.canSubmit, state.isSubmitting]}
                  children={([canSubmit, isSubmitting]) => (
                    <>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => navigate(-1)}
                        className={
                          'cursor-pointer rounded-lg px-6 py-4 text-base text-gray-600 hover:bg-gray-100'
                        }
                      >
                        {t('booking.symptomChecker.back')}
                      </Button>
                      <Button
                        type="submit"
                        disabled={!canSubmit || isSubmitting}
                        className={
                          'cursor-pointer rounded-lg bg-green-600 px-8 py-4 text-base hover:bg-green-700'
                        }
                      >
                        {isSubmitting
                          ? t('booking.symptomChecker.submitting')
                          : t('secondOpinion.symptomChecker.continue')}
                      </Button>
                    </>
                  )}
                />
              </div>
              <form.Subscribe
                selector={(state) => state.errors}
                children={(errors) => <FormMessage errors={errors} />}
              />
            </form>
          </CardContent>
        </Card>
      </div>
    </>
  );
}

export default function SecondOpinionSymptomChecker() {
  const data = useLoaderData() as Awaited<
    ReturnType<typeof secondOpinionSymptomCheckerLoader>
  >;
  if (data.identity.b2cProfile) return <B2CSecondOpinionChecker />;
  return <B2BSecondOpinionChecker />;
}

export async function secondOpinionSymptomCheckerLoader() {
  const identity = await requiresAuth();
  return { identity };
}
